import React, { useCallback, useEffect, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Link as MuiLink,
  Pagination,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import Container from "../../components/ui/ui kit/Container";
import { SearchLeiCode } from "../../api/services/search-lei";
import { ILei } from "../../types/lei";
import { transformLei, searchGleifLei } from "../../utils/lei";
import { useDebounce } from "../../hooks/debounce";
import AdSlot from "../../components/functional/Ads/AdSlot";
import { muiTheme } from "../../theme";

const PAGE_SIZE_COUNT = 499; // matches GLEIF's own public page cap, as before

const formatDate = (date: string) => date ? date.slice(0, 19).replace("T", " ") : "n/a";

export const Search = () => {
  const location = useLocation();
  const pageUrl = window.location.origin + location.pathname;
  const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

  const [leis, setLeis] = useState<ILei[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query);
  const isSearching = debouncedQuery.trim().length > 0;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const fetchPage = useCallback(async (pageNumber: number) => {
    setLoading(true);
    try {
      const res = await SearchLeiCode.requestSearchLeiCode(pageNumber);
      setLeis(transformLei(res.data.data));
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (debouncedQuery.trim().length > 2) {
      setLoading(true);
      searchGleifLei(debouncedQuery.trim())
        .then(setLeis)
        .catch(console.log)
        .finally(() => setLoading(false));
    } else if (debouncedQuery.trim().length === 0) {
      fetchPage(page);
    }
  }, [debouncedQuery, page, fetchPage]);

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  return (
    <Container>
      <Helmet>
        {/* When making changes here, make also changes to server.js file accordingly */}
        <title>Search LEI Records with LEI Search at LEIpapa</title>
        <meta name="description" content="Search LEI Records with LEIpapa LEI Search module. Users can search LEI within the whole LEI database. Search by Legal Entity or LEI code." />
        <link rel="canonical" href={pageUrl} />
        {/* OpenGraph */}
        <meta property="og:title" content="Search LEI Records with LEI Search at LEIpapa" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://app.leipapa.com/leipapa.png" />
        <meta property="og:url" content={pageUrl} />
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@leipapa_com" />
        <meta name="twitter:title" content="Search LEI Records with LEI Search at LEIpapa" />
        <meta name="twitter:description" content="Search LEI Records with LEIpapa LEI Search module. Users can search LEI within the whole LEI database. Search by Legal Entity or LEI code." />
        <meta name="twitter:image" content="https://app.leipapa.com/leipapa.png" />
      </Helmet>
        <Box sx={{ py: { xs: 3, sm: 4 } }}>
          <Typography variant="h4" component="h1" fontWeight={700} gutterBottom sx={{ color: "primary.dark" }}>
            Search for LEI Records
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Search for an LEI record. Users can search within the whole LEI database.
            Searches can be performed by Legal Entity, LEI code, and several other values.
            LEI codes that have "LAPSED" status for more than 1 year may not be visible in the search.
          </Typography>

          <TextField
            fullWidth
            placeholder="Search for an LEI"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: "text.disabled", mr: 1 }} />,
            }}
            sx={{
              mb: 3,
              // This project's global Tailwind/Flowbite CSS puts its own
              // focus box-shadow ring directly on <input> elements. Since
              // that ring only wraps the input's own box (which starts
              // after the icon, not the fieldset's), it visually splits
              // MUI's own outlined border in two. MUI's border-color
              // change already shows focus, so drop the inherited ring.
              "& .MuiOutlinedInput-input:focus": {
                boxShadow: "none",
              },
            }}
          />

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : leis.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
              No data found
            </Typography>
          ) : isMobile ? (
            <Stack spacing={1.5}>
              {leis.map((lei, index) => (
                <Card key={`${lei.lei}-${index}`} variant="outlined">
                  <CardContent>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                      <Typography variant="subtitle1" fontWeight={600}>
                        {lei.name}
                      </Typography>
                      <Chip
                        size="small"
                        label={lei.status || "n/a"}
                        color={lei.status === "ISSUED" ? "success" : "error"}
                      />
                    </Stack>
                    <MuiLink component={RouterLink} to={`/lei-details/${lei.lei}`} sx={{ display: "block", mt: 0.5 }}>
                      {lei.lei}
                    </MuiLink>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {lei.country} &middot; Next renewal: {formatDate(lei.date)}
                    </Typography>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>LEI</TableCell>
                    <TableCell>Legal Name</TableCell>
                    <TableCell>Country</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Next renewal date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {leis.map((lei, index) => (
                    <TableRow key={`${lei.lei}-${index}`} hover>
                      <TableCell>
                        <MuiLink component={RouterLink} to={`/lei-details/${lei.lei}`}>
                          {lei.lei}
                        </MuiLink>
                      </TableCell>
                      <TableCell>{lei.name}</TableCell>
                      <TableCell>{lei.country}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={lei.status || "n/a"}
                          color={lei.status === "ISSUED" ? "success" : "error"}
                        />
                      </TableCell>
                      <TableCell>{formatDate(lei.date)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          {!isSearching && !loading && leis.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
              <Pagination
                count={PAGE_SIZE_COUNT}
                page={page}
                onChange={handlePageChange}
                siblingCount={isMobile ? 0 : 1}
                shape="rounded"
              />
            </Box>
          )}
        </Box>

      <Box sx={{ mt: 4 }}>
        <AdSlot placement="search_bottom" />
      </Box>
    </Container>
  );
};
